import React, { useEffect, useRef, useState } from 'react'
import { useCandidateChat } from '../CandidateChatContext'
import { useIsMobile, useOrientation } from '../../../../hooks/useMediaQuery'
import { useTranslation } from '../../../../hooks/useTranslation'
import { useSilenceAudio } from './useSilenceAudio'

/**
 * VideoConversation component - handles video chat display and controls
 * Based on the op_next design from app/views/home/coach_chat/content/_video_conversation.html.erb
 *
 * @param {Object} props
 * @param {string} props.chatMode - Current chat mode (text, audio, video)
 * @param {string} props.chatStatus - Current chat status
 * @param {Object} props.chatManager - Chat manager instance
 */
const VideoConversation = ({
  chatMode,
  hasLocalVideo,
  hasRemoteVideo,
  connectionState,
}) => {
  const { candidateChatManager, connectionState: contextConnectionState } =
    useCandidateChat()
  const { t } = useTranslation('coach_chat')
  const selfVideoRef = useRef(null)
  const callerVideoRef = useRef(null)
  const remoteVideoContainerRef = useRef(null)
  const [remoteVideoHeight, setRemoteVideoHeight] = useState(null)
  const [remoteVideoAspectRatio, setRemoteVideoAspectRatio] = useState(16 / 9) // Default to 16:9
  const [useSideBySideLayout, setUseSideBySideLayout] = useState(false)

  // Video pause state
  const [isVideoPaused, setIsVideoPaused] = useState(false)
  const isAudioMuted =
    connectionState?.audioMuted || contextConnectionState?.audioMuted || false

  // Mobile detection and orientation using hooks
  const isMobile = useIsMobile()
  const orientation = useOrientation()

  // Use silence audio to keep connection alive when tab loses focus
  useSilenceAudio({
    chatStatus: connectionState?.chatStatus,
    chatMode: chatMode,
  })

  // Listen for video pause state changes
  useEffect(() => {
    const handleVideoPauseChanged = (data) => {
      setIsVideoPaused(data.paused)
    }

    if (candidateChatManager) {
      candidateChatManager.addEventListener(
        'videoPauseChanged',
        handleVideoPauseChanged,
      )

      // Get initial pause state
      const mediaState = candidateChatManager.mediaService?.getMediaState()
      if (mediaState) {
        setIsVideoPaused(mediaState.videoPaused)
      }
    }

    return () => {
      if (candidateChatManager) {
        candidateChatManager.removeEventListener(
          'videoPauseChanged',
          handleVideoPauseChanged,
        )
      }
    }
  }, [candidateChatManager])

  // Set up local video stream when component mounts or hasLocalVideo changes
  useEffect(() => {
    /*
    console.log('VideoConversation - Local video effect triggered:', {
      hasLocalVideo,
      hasConnectionState: !!connectionState,
      localStream: connectionState?.localStream,
      videoElement: !!selfVideoRef.current,
    })
    */

    if (hasLocalVideo && selfVideoRef.current && connectionState) {
      const localStream = connectionState?.localStream
      if (localStream && selfVideoRef.current.srcObject !== localStream) {
        // console.log('VideoConversation - Setting local video stream')
        selfVideoRef.current.srcObject = localStream
        selfVideoRef.current.muted = true
      }
    }
  }, [hasLocalVideo, connectionState])

  // Set up remote video stream when component mounts or hasRemoteVideo changes
  useEffect(() => {
    /*
    console.log('VideoConversation - Remote video effect triggered:', {
      hasRemoteVideo,
      hasConnectionState: !!connectionState,
      remoteStream: connectionState?.remoteStream,
      videoElement: !!callerVideoRef.current,
    })
    */

    if (hasRemoteVideo && callerVideoRef.current && connectionState) {
      const remoteStream = connectionState?.remoteStream
      if (remoteStream && callerVideoRef.current.srcObject !== remoteStream) {
        // console.log('VideoConversation - Setting remote video stream')
        callerVideoRef.current.srcObject = remoteStream

        // Ensure audio playback is enabled
        callerVideoRef.current.muted = false
        callerVideoRef.current.volume = 1.0

        // Try to play the video to ensure audio starts
        callerVideoRef.current.play().catch((error) => {
          console.warn('VideoConversation - Could not auto-play video:', error)
        })
      }
    }
  }, [hasRemoteVideo, connectionState])

  // Get aspect ratio from video stream when metadata loads
  useEffect(() => {
    const handleRemoteVideoMetadata = () => {
      if (callerVideoRef.current) {
        const video = callerVideoRef.current
        if (video.videoWidth && video.videoHeight) {
          const aspectRatio = video.videoWidth / video.videoHeight
          setRemoteVideoAspectRatio(aspectRatio)
        }
      }
    }

    const remoteVideo = callerVideoRef.current
    if (remoteVideo) {
      remoteVideo.addEventListener('loadedmetadata', handleRemoteVideoMetadata)
      // Check if already loaded
      if (remoteVideo.readyState >= 1) {
        handleRemoteVideoMetadata()
      }
    }

    return () => {
      if (remoteVideo) {
        remoteVideo.removeEventListener(
          'loadedmetadata',
          handleRemoteVideoMetadata,
        )
      }
    }
  }, [hasRemoteVideo])

  // Calculate and set height based on actual aspect ratio (desktop only)
  useEffect(() => {
    if (isMobile) return // Skip for mobile

    const calculateHeight = () => {
      if (remoteVideoContainerRef.current) {
        const container =
          remoteVideoContainerRef.current.closest('.caller-vid-wrap')
        if (!container) return

        const containerWidth = container.offsetWidth
        let videoWidth = containerWidth
        let height

        // Check if we should use side-by-side layout
        const viewportHeight = window.innerHeight
        const thirtyVh = viewportHeight * 0.3

        // Calculate height for full-width layout first
        height = Math.round(containerWidth / remoteVideoAspectRatio)
        const shouldUseSideBySide = height > thirtyVh

        setUseSideBySideLayout(shouldUseSideBySide)

        // If side-by-side, calculate based on half width (accounting for gap-3 = 12px)
        if (shouldUseSideBySide) {
          const gap = 12 // gap-3 = 0.75rem = 12px
          videoWidth = (containerWidth - gap) / 2
          height = Math.round(videoWidth / remoteVideoAspectRatio)
        }

        setRemoteVideoHeight(height)
      }
    }

    // Calculate on mount and resize
    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [isMobile, remoteVideoAspectRatio])

  // Only show for video chat mode when local video is ready
  if (chatMode !== 'video' || !hasLocalVideo) {
    return null
  }

  // Show paused state if video is paused (banner clears automatically on resume)
  const showPausedState = isVideoPaused
  const pauseBannerCopy = isAudioMuted
    ? t('video_status.video_and_audio_paused', {
        defaultValue:
          "Video and audio are paused. The coach can't see or hear you. To resume video and audio, use the controls in the frame above. You may also continue to text chat below.",
      })
    : t('video_status.video_paused_only', {
        defaultValue:
          'Video is paused. The coach can\'t see you but can still hear you. To mute your audio, click the mute button above. Click the "Video" button to resume video.',
      })

  // Determine layout classes based on mobile and orientation
  const getLayoutClasses = () => {
    if (!isMobile) {
      // Desktop layout - side by side if remote video height > 30vh
      if (useSideBySideLayout) {
        return {
          container:
            'conversation-container video-conversation-container flex min-h-0 flex-1 flex-col items-center justify-center border-b border-solid border-gray-800 p-0 pt-2 pb-2 text-center',
          videoWrap: 'caller-vid-wrap flex gap-3 m-auto w-full object-contain',
          callerContainer:
            'caller-vid-container relative flex-1 rounded-lg bg-black',
          myVideoContainer:
            'my-vid-container relative flex-1 rounded-lg border border-solid border-orange-500 bg-black',
          remoteVideoClasses:
            'remote-video w-full h-full rounded-lg object-contain',
          localVideoClasses:
            'easyrtcMirror local-video -scale-x-100 w-full h-full rounded-lg object-contain',
        }
      }

      // Desktop layout - full width with 16:9 aspect ratio (default)
      return {
        container:
          'conversation-container video-conversation-container flex min-h-0 flex-1 flex-col items-center justify-center border-b border-solid border-gray-800 p-0 pt-2 pb-12 text-center',
        videoWrap: 'caller-vid-wrap relative m-auto w-full object-contain',
        callerContainer:
          'caller-vid-container relative w-full rounded-lg bg-black',
        myVideoContainer:
          'my-vid-container absolute -bottom-12 right-1.5 z-10 rounded-lg border border-solid border-orange-500 bg-black',
        remoteVideoClasses:
          'remote-video w-full h-full rounded-lg object-contain',
        localVideoClasses:
          'easyrtcMirror local-video -scale-x-100 h-auto max-w-[150px] rounded-lg',
      }
    }

    if (orientation === 'portrait') {
      // Mobile portrait - stacked layout with Flexbox
      return {
        container:
          'conversation-container video-conversation-container h-full w-full flex flex-col min-h-0 overflow-hidden p-0 border-b border-solid border-gray-800',
        videoWrap: 'caller-vid-wrap h-full w-full min-h-0 flex flex-col p-0',
        callerContainer:
          'caller-vid-container h-full w-full min-h-0 flex-[2] overflow-hidden',
        myVideoContainer:
          'my-vid-container h-full w-full min-h-0 flex-[1] p-0 bg-black overflow-hidden',
        remoteVideoClasses:
          'remote-video h-full w-full max-w-full rounded-lg object-contain',
        localVideoClasses:
          'easyrtcMirror local-video -scale-x-100 h-full w-full rounded-lg object-contain',
      }
    } else {
      // Mobile landscape - flexbox with absolute positioning
      return {
        container:
          'conversation-container video-conversation-container h-full w-full flex flex-col p-0 bg-black border-b border-solid border-gray-800',
        videoWrap:
          'caller-vid-wrap w-full h-full max-w-full max-h-[calc(100vh-64px)] flex items-center justify-center bg-black',
        callerContainer: 'caller-vid-container h-[calc(100vh-64px)] w-full',
        myVideoContainer:
          'my-vid-container absolute bottom-2.5 right-2.5 h-[150px] aspect-[16/9] z-10',
        remoteVideoClasses: 'remote-video h-full max-w-full object-cover',
        localVideoClasses:
          'easyrtcMirror local-video -scale-x-100 h-full w-full object-cover',
      }
    }
  }

  const layoutClasses = getLayoutClasses()

  return (
    <div className={layoutClasses.container}>
      {/* Paused status banner (CC-UX-023) */}
      {showPausedState && (
        <div className="paused-video flex min-h-[160px] w-full items-center justify-center bg-black px-5 py-8 text-left">
          <div className="paused-video-text mx-auto max-w-xl font-roboto text-sm leading-relaxed text-white md:text-base">
            {pauseBannerCopy}
          </div>
        </div>
      )}
      {/* Video display area */}
      <div
        className={layoutClasses.videoWrap}
        style={{
          display: showPausedState
            ? 'none'
            : useSideBySideLayout
              ? 'flex'
              : 'block',
        }}
      >
        {/* Remote video container */}
        <div
          ref={!isMobile ? remoteVideoContainerRef : null}
          className={layoutClasses.callerContainer}
          style={
            !isMobile && remoteVideoHeight
              ? { height: `${remoteVideoHeight}px` }
              : undefined
          }
        >
          {/* Remote video */}
          <video
            ref={callerVideoRef}
            autoPlay
            playsInline
            muted={false}
            id="callerVideo"
            className={`${layoutClasses.remoteVideoClasses} ${isVideoPaused ? 'opacity-0' : ''}`}
            aria-label="Remote video stream"
          />
        </div>

        {/* Local video container */}
        <div
          className={`${layoutClasses.myVideoContainer} ${isVideoPaused ? 'opacity-0' : ''}`}
          style={
            !isMobile && useSideBySideLayout && remoteVideoHeight
              ? { height: `${remoteVideoHeight}px` }
              : undefined
          }
        >
          <video
            ref={selfVideoRef}
            autoPlay
            playsInline
            id="selfVideo"
            className={layoutClasses.localVideoClasses}
            muted
            aria-label="Local video stream"
          />
        </div>
      </div>
    </div>
  )
}

export default VideoConversation
